import { attr, belongsTo } from "@ember-data/model";
import type Store from "@ember-data/store";
import { service } from "@ember/service";

import dayjs from "dayjs";
import {
  validateDate,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";
import tWithDefault from "parog-web/utils/t-with-default";
import type { SerializedSurvey } from "./compound-survey";
import type CompoundSurvey from "./compound-survey";

//---- Automatically generated, don't update this part ----

export default class CompoundSurveyCompletedJournalEvent extends JournalEvent {
  static override type = "compound-survey-completed-journal-event";

  /**
   * The id of the corresponding CompoundSurvey
   */
  @attr("string") declare surveyIdentifier: string;

  /**
   * Contains the answers corresponding to the associated survey.
   * The keys represent the CompoundQuestion ids and the value and the values the index of the answer
   */
  @attr({
    defaultValue() {
      return {};
    }
  })
  declare answers: any;

  static override validations(intl: IntlService) {
    return {
      surveyIdentifier: validatePresence({
        presence: true,
        description: intl.t(
          "validations.compound-survey-completed-journal-event.survey-identifier"
        )
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: tWithDefault(
            intl,
            "validations.compound-survey-completed-journal-event.occurred-at",
            "La date"
          )
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée avant 1900. Veuillez modifier la date",
          onOrAfter: new Date("01/01/1900")
        }),
        validateDate({
          message:
            "Cette donnée ne peut être déclarée dans le futur. Veuillez modifier la date",
          onOrBefore: dayjs().endOf("day").toDate()
        })
      ]
    };
  }

  //---- End of generated code ----
  @belongsTo("patient", {
    async: false,
    inverse: "compoundSurveyCompletedJournalEvents"
  })
  declare patient: Patient;

  /**
   * Contains the schema of the associated survey when it has been answered.
   * We keep it in case the survey is modified in the meantime
   */
  @attr({
    defaultValue() {
      return [];
    }
  })
  declare surveyRaw: SerializedSurvey;

  @service declare store: Store;

  /**
   * Returns the score of the survey
   */
  get score(): number {
    return (Object.values(this.answers) as number[]).reduce(
      (acc, curr) => acc + curr,
      0
    );
  }

  get survey(): CompoundSurvey | null {
    return this.store.peekRecord("compound-survey", this.surveyIdentifier);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module "ember-data/types/registries/model" {
  export default interface ModelRegistry {
    "compound-survey-completed-journal-event": CompoundSurveyCompletedJournalEvent;
  }
}
